import { createSlice } from '@reduxjs/toolkit';
import SubscriptionPlanService from '../../services/subscriptionPlan';

/**
 * State shape:
 * {
 *   isLoading: bool,
 *   isSaving: bool,
 *   error: false | string,
 *   unsaved: bool,           // true when user has unsaved local edits
 *   matrix: null | {
 *     plans: string[],       // ['starter', 'advance', 'complete']
 *     planNames: Record<string, string>,
 *     prices: Record<string, { monthly_cents: number, yearly_cents: number }>,
 *     groups: Array<{
 *       group: string,
 *       entitlements: Array<{
 *         key: string, label: string, description: string|null,
 *         value_type: 'bool'|'int',
 *         plans: Record<string, { enabled: bool, value_int: number|null }>
 *       }>
 *     }>,
 *     upgrade_links: Record<string, { url: string, is_active: bool }>
 *   }
 * }
 */
const initialState = {
  isLoading: false,
  isSaving: false,
  error: false,
  unsaved: false,
  matrix: null
};

const slice = createSlice({
  name: 'subscriptionPlan',
  initialState,
  reducers: {
    startLoading(state) {
      state.isLoading = true;
      state.error = false;
    },
    startSaving(state) {
      state.isSaving = true;
      state.error = false;
    },
    hasError(state, action) {
      state.isLoading = false;
      state.isSaving = false;
      state.error = action.payload;
    },
    getMatrixSuccess(state, action) {
      state.isLoading = false;
      state.matrix = action.payload;
      state.unsaved = false;
    },
    saveMatrixSuccess(state) {
      state.isSaving = false;
      state.unsaved = false;
    },

    /** Optimistic local update for a price cell; marks state as unsaved */
    updatePrice(state, action) {
      const { planId, field, value } = action.payload; // field: 'monthly_cents' | 'yearly_cents'
      if (state.matrix?.prices?.[planId] !== undefined) {
        state.matrix.prices[planId][field] = value;
        state.unsaved = true;
      }
    },

    /** Optimistic local update for an entitlement cell; marks state as unsaved */
    updateEntitlement(state, action) {
      const { groupIdx, entIdx, planId, field, value } = action.payload;
      // field: 'enabled' | 'value_int'
      const cell = state.matrix?.groups?.[groupIdx]?.entitlements?.[entIdx]?.plans?.[planId];
      if (cell !== undefined) {
        // Write through the Immer draft proxy directly (avoids redundant re-traversal)
        cell[field] = value;
        state.unsaved = true;
      }
    },

    /** Optimistic local update for an upgrade link field; marks state as unsaved */
    updateUpgradeLink(state, action) {
      const { planId, field, value } = action.payload; // field: 'url' | 'is_active'
      if (state.matrix?.upgrade_links?.[planId] !== undefined) {
        state.matrix.upgrade_links[planId][field] = value;
        state.unsaved = true;
      }
    }
  }
});

export default slice.reducer;

export const {
  updatePrice,
  updateEntitlement,
  updateUpgradeLink
} = slice.actions;

// ---------------------------------------------------------------------------
// Thunks
// ---------------------------------------------------------------------------

/** Fetch pricing matrix from API and populate Redux state */
export function fetchMatrix(currency = 'USD') {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await SubscriptionPlanService.getMatrix(currency);
      if (response.success) {
        dispatch(slice.actions.getMatrixSuccess(response.data));
      } else {
        dispatch(slice.actions.hasError(response.errors?.[0]?.message || 'Failed to fetch matrix'));
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error.message || 'Failed to fetch matrix'));
    }
  };
}

/**
 * Atomically save current matrix state to API.
 * Returns { success: bool, message: string } so the UI can show snackbar feedback.
 */
export function saveMatrix(payload) {
  return async (dispatch) => {
    dispatch(slice.actions.startSaving());
    try {
      const response = await SubscriptionPlanService.saveMatrix(payload);
      if (response.success) {
        dispatch(slice.actions.saveMatrixSuccess());
        return { success: true, message: response.message || 'Pricing matrix saved successfully' };
      }
      const errMsg = response.errors?.[0]?.message || response.message || 'Failed to save matrix';
      dispatch(slice.actions.hasError(errMsg));
      return { success: false, message: errMsg };
    } catch (error) {
      const msg = error.message || 'Failed to save matrix';
      dispatch(slice.actions.hasError(msg));
      return { success: false, message: msg };
    }
  };
}
